
import { defineComponent, ref, computed, onMounted, onUnmounted, watch } from 'vue';
import {
    IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons,
    IonBackButton, IonIcon, IonSpinner,
    loadingController, alertController,
} from '@ionic/vue';
import { useStore } from 'vuex';
import {
    playOutline, pauseOutline, trashOutline, eyeOutline, cloudUploadOutline, globeOutline, createOutline,
    documentAttachOutline, musicalNotesOutline, imageOutline, videocamOutline, linkOutline,
} from 'ionicons/icons';
import { GET, DELETE, FILEUPLOAD, POST } from '@/services/HTTP';
import { formatSizeKb, hasKnownMediaSizeKb, isLibraryAudioReady } from '@/utils/mediaSizeDisplay';
import { waitUntilAudioReadyToPlay } from '@/utils/audioBlobPlayback';
import { uuid4 } from '@capacitor/core/dist/esm/util';

const AUDIO_GROUP_TYPE = 'cc61a205-70f7-4b8c-92a0-0a4ea2b93f22';
const IMAGE_GROUP_TYPE = 'cc61a205-70f7-4b8c-92a0-0a4ea2b93f21';

interface AudioFile {
    id: string;
    name: string;
    createdDate: string;
    sizeKb: number | null;
    processingMsg: string | null;
    instanceId: string | null;
    audioGroupId: string;
    inUseCnt: number;
}

interface ImageFile {
    id: string;
    filename: string;
    instanceId: string | null;
    sizeKb: number | null;
    processingMsg: string | null;
    inUseCnt: number;
}

interface AudioGroup {
    id: string;
    name: string;
    imageGroupTypeId: string;
    audioFiles: AudioFile[];
    images: ImageFile[];
}

interface InUseInfo {
    inUse: boolean;
    journeyCount: number;
    tabCount: number;
}

interface ResourceItem {
    id: string;
    name: string;
    mimeType: string;
    resourceLabel: string | null;
    url: string | null;
    inUseCnt: number;
    appInstanceId: string;
    shareable: boolean;
}

export default defineComponent({
    name: 'AudioLibraryManager',
    components: {
        IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons,
        IonBackButton, IonIcon, IonSpinner,
    },
    setup() {
        const store = useStore();
        const instanceId = computed(() => store.getters['app/selectedAppInstanceId']);

        const loadingGroups = ref(false);
        const loadingFiles = ref(false);
        const loadingResources = ref(false);
        const groups = ref<AudioGroup[]>([]);
        const selectedGroup = ref<AudioGroup | null>(null);
        const selectedView = ref<'group' | 'resources' | null>(null);
        const audioFiles = ref<AudioFile[]>([]);
        const imageFiles = ref<ImageFile[]>([]);
        const resources = ref<ResourceItem[]>([]);
        const deleteTargetResource = ref<ResourceItem | null>(null);
        const resourceUploadModal = ref({
            open: false,
            step: 'select-type' as 'select-type' | 'confirm-file' | 'url' | 'edit',
            name: '',
            label: '',
            url: '',
            shareable: false,
            saving: false,
            fileData: null as string | null,
            mimeType: '',
            editId: null as string | null,
        });
        const searchQuery = ref('');
        const sortBy = ref('date-desc');
        const resourceTypeFilter = ref<string>('all');
        const playingId = ref<string | null>(null);
        const currentAudio = ref<HTMLAudioElement | null>(null);
        const deletingId = ref<string | null>(null);
        const deleteTarget = ref<AudioFile | null>(null);
        const deleteImageTarget = ref<ImageFile | null>(null);
        const iconPreview = ref<{ filename: string; objectUrl: string } | null>(null);
        const iconPreviewLoadingId = ref<string | null>(null);
        const inUseInfo = ref<InUseInfo | null>(null);

        const groupFileCount = (group: AudioGroup) => {
            const aud = group.audioFiles?.length ?? 0;
            const imgs = group.images ?? [];
            const uniqueImages = new Set(imgs.map(i => i.id)).size;
            return aud + uniqueImages;
        };

        /** Dedupes by id (API can duplicate rows); search matches filename. */
        const filteredImageFiles = computed(() => {
            const q = searchQuery.value.trim().toLowerCase();
            const seen = new Set<string>();
            const unique = imageFiles.value.filter(f => {
                if (seen.has(f.id)) return false;
                seen.add(f.id);
                return true;
            });
            if (!q) return unique;
            return unique.filter(f => (f.filename ?? '').toLowerCase().includes(q));
        });

        const resourceIcon = (mimeType: string) => {
            if (!mimeType) return linkOutline;
            if (mimeType === 'text/x-uri' || mimeType === 'text/uri-list') return globeOutline;
            if (mimeType === 'application/pdf') return documentAttachOutline;
            if (mimeType.startsWith('audio/')) return musicalNotesOutline;
            if (mimeType.startsWith('image/')) return imageOutline;
            if (mimeType.startsWith('video/')) return videocamOutline;
            return linkOutline;
        };

        const uploadAccept = computed(() => {
            if (!selectedGroup.value) return '';
            const tid = selectedGroup.value.imageGroupTypeId?.toLowerCase();
            if (tid === IMAGE_GROUP_TYPE) return '.svg,image/svg+xml';
            return '.mp3,.wav,.m4a,.aac,.ogg,.flac';
        });

        const filteredFiles = computed(() => {
            let list = audioFiles.value;
            const q = searchQuery.value.trim().toLowerCase();
            if (q) list = list.filter(f => f.name.toLowerCase().includes(q));
            return [...list].sort((a, b) => {
                switch (sortBy.value) {
                    case 'date-asc':  return new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime();
                    case 'name-asc':  return a.name.localeCompare(b.name);
                    case 'name-desc': return b.name.localeCompare(a.name);
                    case 'size-desc': return (b.sizeKb ?? -1) - (a.sizeKb ?? -1);
                    case 'size-asc':  return (a.sizeKb ?? Infinity) - (b.sizeKb ?? Infinity);
                    default:          return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
                }
            });
        });

        const resourceTypeBuckets = computed(() => {
            const buckets = new Set<string>();
            for (const r of resources.value) {
                const m = r.mimeType ?? '';
                if (m === 'text/x-uri' || m === 'text/uri-list') buckets.add('link');
                else if (m === 'application/pdf') buckets.add('pdf');
                else if (m.startsWith('audio/')) buckets.add('audio');
                else if (m.startsWith('image/')) buckets.add('image');
                else if (m.startsWith('video/')) buckets.add('video');
                else buckets.add('other');
            }
            return buckets;
        });

        const BUCKET_LABELS: Record<string, string> = {
            link: 'Links', pdf: 'PDF', audio: 'Audio',
            image: 'Images', video: 'Video', other: 'Other',
        };

        const mimeTypeBucket = (mimeType: string) => {
            const m = mimeType ?? '';
            if (m === 'text/x-uri' || m === 'text/uri-list') return 'link';
            if (m === 'application/pdf') return 'pdf';
            if (m.startsWith('audio/')) return 'audio';
            if (m.startsWith('image/')) return 'image';
            if (m.startsWith('video/')) return 'video';
            return 'other';
        };

        const filteredResources = computed(() => {
            let list = resources.value;
            if (resourceTypeFilter.value !== 'all') {
                list = list.filter(r => mimeTypeBucket(r.mimeType ?? '') === resourceTypeFilter.value);
            }
            const q = searchQuery.value.trim().toLowerCase();
            if (q) list = list.filter(r =>
                r.name.toLowerCase().includes(q) ||
                (r.resourceLabel ?? '').toLowerCase().includes(q)
            );
            return list;
        });

        const stopAudio = () => {
            if (currentAudio.value) {
                currentAudio.value.pause();
                if (currentAudio.value.src?.startsWith('blob:')) {
                    URL.revokeObjectURL(currentAudio.value.src);
                }
                currentAudio.value = null;
            }
            playingId.value = null;
        };

        const dedupeImagesById = (images: ImageFile[] | undefined): ImageFile[] => {
            if (!images?.length) return [];
            const seen = new Set<string>();
            return images.filter(img => {
                if (seen.has(img.id)) return false;
                seen.add(img.id);
                return true;
            });
        };

        const selectGroup = async (group: AudioGroup) => {
            selectedGroup.value = group;
            selectedView.value = 'group';
            searchQuery.value = '';
            stopAudio();
            audioFiles.value = group.audioFiles ?? [];
            imageFiles.value = dedupeImagesById(group.images);
        };

        const selectResources = () => {
            selectedGroup.value = null;
            selectedView.value = 'resources';
            searchQuery.value = '';
            resourceTypeFilter.value = 'all';
            stopAudio();
        };

        const loadingError = ref<string | null>(null);

        const loadGroups = async () => {
            if (!instanceId.value) {
                loadingGroups.value = false;
                loadingError.value = 'Select an app instance on the Home screen to see files for that app.';
                groups.value = [];
                resources.value = [];
                selectedGroup.value = null;
                selectedView.value = null;
                audioFiles.value = [];
                imageFiles.value = [];
                return;
            }
            loadingGroups.value = true;
            loadingError.value = null;
            try {
                const [groupsResult, resourcesResult] = await Promise.all([
                    GET(`/audio/groups/instance/${instanceId.value}`, false),
                    GET(`/instances/${instanceId.value}/resources`, false),
                ]);
                groups.value = groupsResult ?? [];
                resources.value = resourcesResult ?? [];
                // Auto-select the first group if there's only one and no resources
                if (groups.value.length === 1 && resources.value.length === 0) {
                    selectGroup(groups.value[0]);
                }
            } catch (e) {
                loadingError.value = 'Failed to load files.';
                console.error('[AudioLibraryManager] loadGroups error:', e);
            } finally {
                loadingGroups.value = false;
            }
        };

        const togglePlay = async (file: AudioFile) => {
            if (playingId.value === file.id) { stopAudio(); return; }
            stopAudio();
            playingId.value = file.id;
            try {
                const result = await GET(`/audio/${file.id}/stream`, true);
                if (playingId.value !== file.id) return;
                if (!result.blob.size) {
                    playingId.value = null;
                    const alert = await alertController.create({
                        header: 'Playback failed',
                        message: 'The audio file is empty or still processing.',
                        buttons: ['OK'],
                    });
                    await alert.present();
                    return;
                }
                const objectUrl = URL.createObjectURL(result.blob);
                const audio = new Audio();
                audio.preload = 'auto';
                audio.src = objectUrl;
                audio.onended = () => {
                    playingId.value = null;
                    currentAudio.value = null;
                    URL.revokeObjectURL(objectUrl);
                };
                currentAudio.value = audio;
                await waitUntilAudioReadyToPlay(audio);
                await audio.play();
            } catch {
                stopAudio();
                const alert = await alertController.create({
                    header: 'Playback failed',
                    message: 'Could not play this file. If it was just uploaded, wait for processing to finish.',
                    buttons: ['OK'],
                });
                await alert.present();
            }
        };

        const confirmDelete = async (file: AudioFile) => {
            inUseInfo.value = null;
            deleteTarget.value = file;
            try {
                inUseInfo.value = await GET(`/audio/${file.id}/in-use`, false);
            } catch {
                // proceed without in-use info
            }
        };

        const confirmDeleteImage = (file: ImageFile) => {
            deleteImageTarget.value = file;
        };

        const closeIconPreview = () => {
            if (iconPreview.value?.objectUrl) {
                URL.revokeObjectURL(iconPreview.value.objectUrl);
            }
            iconPreview.value = null;
        };

        const openIconPreview = async (file: ImageFile) => {
            closeIconPreview();
            iconPreviewLoadingId.value = file.id;
            try {
                const res = await GET(`/images/${file.id}/src`, true) as { blob: Blob };
                const objectUrl = URL.createObjectURL(res.blob);
                iconPreview.value = { filename: file.filename, objectUrl };
            } catch {
                const alert = await alertController.create({
                    header: 'Preview failed',
                    message: 'Could not load this icon from storage.',
                    buttons: ['OK'],
                });
                await alert.present();
            } finally {
                iconPreviewLoadingId.value = null;
            }
        };

        const deleteFile = async () => {
            if (!deleteTarget.value) return;
            const file = deleteTarget.value;
            deletingId.value = file.id;
            deleteTarget.value = null;
            try {
                await DELETE(`/audio/${file.id}`);
                audioFiles.value = audioFiles.value.filter(f => f.id !== file.id);
                if (selectedGroup.value) {
                    selectedGroup.value.audioFiles = selectedGroup.value.audioFiles.filter(f => f.id !== file.id);
                }                if (playingId.value === file.id) stopAudio();
            } catch (e) {
                const alert = await alertController.create({
                    header: 'Delete Failed',
                    message: typeof e === 'string' && e ? e : 'Unable to delete this file.',
                    buttons: ['OK'],
                });
                await alert.present();
            } finally {
                deletingId.value = null;
            }
        };

        const deleteImageFile = async () => {
            if (!deleteImageTarget.value) return;
            const file = deleteImageTarget.value;
            deletingId.value = file.id;
            deleteImageTarget.value = null;
            try {
                await DELETE(`/images/${file.id}`);
                imageFiles.value = imageFiles.value.filter(f => f.id !== file.id);
                if (selectedGroup.value) {
                    selectedGroup.value.images = selectedGroup.value.images.filter(f => f.id !== file.id);
                }
            } catch (e) {
                const alert = await alertController.create({
                    header: 'Delete Failed',
                    message: typeof e === 'string' && e ? e : 'Unable to delete this file.',
                    buttons: ['OK'],
                });
                await alert.present();
            } finally {
                deletingId.value = null;
            }
        };

        const confirmDeleteResource = (res: ResourceItem) => {
            deleteTargetResource.value = res;
        };

        const openResourceUploadModal = () => {
            resourceUploadModal.value = { open: true, step: 'select-type', name: '', label: '', url: '', shareable: false, saving: false, fileData: null, mimeType: '', editId: null };
        };

        const openResourceUrlModal = () => {
            resourceUploadModal.value = { open: true, step: 'url', name: '', label: '', url: '', shareable: false, saving: false, fileData: null, mimeType: 'text/x-uri', editId: null };
        };

        const editResource = (res: ResourceItem) => {
            resourceUploadModal.value = {
                open: true,
                step: 'edit',
                name: res.name,
                label: res.resourceLabel ?? '',
                url: res.url ?? '',
                shareable: res.shareable,
                saving: false,
                fileData: null,
                mimeType: res.mimeType,
                editId: res.id,
            };
        };

        const closeResourceUploadModal = () => {
            resourceUploadModal.value.open = false;
        };

        const onResourceFileSelected = (event: Event) => {
            const input = event.target as HTMLInputElement;
            if (!input.files?.length) return;
            const file = input.files[0];
            const MAX_SIZE = 7 * 1024 * 1024;
            if (file.size > MAX_SIZE) {
                alertController.create({ header: 'File too large', message: 'Maximum file size is 7 MB.', buttons: ['OK'] }).then(a => a.present());
                input.value = '';
                return;
            }
            const reader = new FileReader();
            reader.onload = () => {
                resourceUploadModal.value.fileData = reader.result as string;
                resourceUploadModal.value.name = file.name;
                resourceUploadModal.value.mimeType = file.type;
                resourceUploadModal.value.step = 'confirm-file';
            };
            reader.readAsDataURL(file);
        };

        const saveResourceUpload = async () => {
            if (!instanceId.value) return;
            const m = resourceUploadModal.value;
            m.saving = true;
            try {
                const isEdit = m.step === 'edit';
                const isUrl = m.step === 'url' || (isEdit && m.mimeType === 'text/x-uri');
                if (isEdit && m.editId) {
                    await POST(`/resources/${m.editId}/update`, {
                        id: m.editId,
                        appInstanceId: instanceId.value,
                        name: m.name,
                        mimeType: m.mimeType,
                        url: isUrl ? (m.url.match(/^https?:\/\//i) ? m.url : `https://${m.url}`) : null,
                        resourceLabel: m.label || null,
                        shareable: m.shareable,
                    });
                } else {
                    const body: Record<string, unknown> = {
                        id: uuid4(),
                        appInstanceId: instanceId.value,
                        name: isUrl ? (m.label || m.url) : m.name,
                        mimeType: isUrl ? 'text/x-uri' : m.mimeType,
                        resourceData: isUrl ? '' : (m.fileData ? m.fileData.split(',')[1] : ''),
                        url: isUrl ? (m.url.match(/^https?:\/\//i) ? m.url : `https://${m.url}`) : null,
                        resourceLabel: m.label || null,
                        shareable: m.shareable,
                    };
                    await POST(`/instances/${instanceId.value}/resources`, body);
                }
                const updated = await GET(`/instances/${instanceId.value}/resources`, false);
                resources.value = updated ?? [];
                closeResourceUploadModal();
            } catch (e) {
                const alert = await alertController.create({ header: 'Save Failed', message: 'Could not save the resource.', buttons: ['OK'] });
                await alert.present();
            } finally {
                m.saving = false;
            }
        };

        const deleteResource = async () => {
            if (!deleteTargetResource.value) return;
            const res = deleteTargetResource.value;
            deletingId.value = res.id;
            deleteTargetResource.value = null;
            try {
                await DELETE(`/instances/${instanceId.value}/resources/${res.id}`);
                resources.value = resources.value.filter(r => r.id !== res.id);
            } catch (e) {
                const alert = await alertController.create({
                    header: 'Delete Failed',
                    message: 'This resource may be in use and cannot be deleted.',
                    buttons: ['OK'],
                });
                await alert.present();
            } finally {
                deletingId.value = null;
            }
        };

        // Poll a newly-uploaded audio file until sizeKb is populated, then update in-place.
        const pollProcessing = (fileId: string) => {
            const INTERVAL = 3000;
            const MAX_ATTEMPTS = 40; // 2 min max
            let attempts = 0;
            const timer = setInterval(async () => {
                attempts++;
                try {
                    const updated: AudioFile = await GET(`/audio/${fileId}`, false);
                    if (updated && (hasKnownMediaSizeKb(updated.sizeKb) || updated.processingMsg)) {
                        clearInterval(timer);
                        const idx = audioFiles.value.findIndex(f => f.id === fileId);
                        if (idx !== -1) {
                            audioFiles.value[idx] = { ...audioFiles.value[idx], sizeKb: updated.sizeKb, processingMsg: updated.processingMsg };
                        }
                        if (selectedGroup.value) {
                            const gi = selectedGroup.value.audioFiles.findIndex(f => f.id === fileId);
                            if (gi !== -1) selectedGroup.value.audioFiles[gi] = { ...selectedGroup.value.audioFiles[gi], sizeKb: updated.sizeKb, processingMsg: updated.processingMsg };
                        }
                    }
                } catch { /* ignore */ }
                if (attempts >= MAX_ATTEMPTS) clearInterval(timer);
            }, INTERVAL);
        };

        const handleUpload = async (event: Event) => {
            if (!selectedGroup.value) return;
            const input = event.target as HTMLInputElement;
            if (!input.files?.length) return;
            const file = input.files[0];
            const isImageGroup = selectedGroup.value.imageGroupTypeId?.toLowerCase() === IMAGE_GROUP_TYPE;

            const loader = await loadingController.create({ message: 'Uploading…' });
            await loader.present();
            try {
                if (isImageGroup) {
                    const formData = new FormData();
                    formData.append('file', file);
                    formData.append('imageGroupId', selectedGroup.value.id);
                    if (instanceId.value) formData.append('instanceId', instanceId.value);

                    const created = await FILEUPLOAD('/images/upload', formData) as ImageFile & { sizeKb?: number | null };
                    if (created?.id) {
                        const row: ImageFile = {
                            id: created.id,
                            filename: created.filename,
                            instanceId: created.instanceId ?? null,
                            sizeKb: created.sizeKb ?? null,
                            processingMsg: null,
                            inUseCnt: created.inUseCnt ?? 0,
                        };
                        imageFiles.value.unshift(row);
                        selectedGroup.value.images = [row, ...selectedGroup.value.images];
                    }
                } else {
                    const formData = new FormData();
                    formData.append('file', file);
                    formData.append('audioGroupId', selectedGroup.value.id);
                    if (instanceId.value) formData.append('instanceId', instanceId.value);

                    const created: AudioFile = await FILEUPLOAD('/audio/upload', formData);
                    if (created) {
                        audioFiles.value.unshift(created);
                        selectedGroup.value.audioFiles = [created, ...selectedGroup.value.audioFiles];
                        if (!hasKnownMediaSizeKb(created.sizeKb)) pollProcessing(created.id);
                    }
                }
            } finally {
                await loader.dismiss();
                input.value = '';
            }
        };

        const handleResourceUpload = async (event: Event) => { void event; }; // replaced by modal

        const formatDate = (iso: string) => {
            if (!iso) return '';
            return new Date(iso).toLocaleDateString();
        };

        watch(instanceId, () => { groups.value = []; resources.value = []; selectedGroup.value = null; selectedView.value = null; audioFiles.value = []; imageFiles.value = []; loadGroups(); }, { immediate: true });
        onMounted(() => store.dispatch('app/setPageTitle', 'My Files'));
        onUnmounted(() => {
            stopAudio();
            closeIconPreview();
        });

        return {
            loadingGroups, loadingFiles, loadingResources, loadingError,
            groups, selectedGroup, selectedView, audioFiles, imageFiles,
            resources, filteredFiles, filteredImageFiles, filteredResources, searchQuery, sortBy,
            resourceTypeFilter, resourceTypeBuckets, BUCKET_LABELS,
            playingId, deletingId, deleteTarget, deleteImageTarget, deleteTargetResource, inUseInfo,
            resourceUploadModal,
            groupFileCount, uploadAccept, resourceIcon,
            selectGroup, selectResources, togglePlay, confirmDelete, deleteFile,
            confirmDeleteImage, deleteImageFile, iconPreview, iconPreviewLoadingId,
            openIconPreview, closeIconPreview,
            confirmDeleteResource, deleteResource, handleUpload, handleResourceUpload,
            openResourceUploadModal, openResourceUrlModal, closeResourceUploadModal, editResource,
            onResourceFileSelected, saveResourceUpload,
            formatDate, formatSizeKb, hasKnownMediaSizeKb, isLibraryAudioReady,
            playOutline, pauseOutline, trashOutline, eyeOutline, cloudUploadOutline, globeOutline, createOutline,
            documentAttachOutline, musicalNotesOutline, imageOutline, videocamOutline, linkOutline,
        };
    },
});
